import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import axios from "../../config/axios-config";

/** A deliberate retry after a timeout reuses the exact request, including its observed line versions. */
export default function useOrderDecision(scope) {
    const {i18n} = useTranslation(), current = useRef(scope), active = useRef(null), attempt = useRef(null);
    current.current = scope;
    const [state, setState] = useState({});
    useEffect(() => {
        attempt.current = null; setState({});
        return () => {active.current?.abort(); active.current = null;};
    }, [scope]);
    const submit = async (order, changes) => {
        if (active.current) { return false; }
        const controller = new AbortController(); active.current = controller;
        const config = {signal: controller.signal, headers: {"Language-Context": i18n.language.toUpperCase()}, timeout: 15000};
        setState({scope, pending: true});
        let body;
        try {
            body = attempt.current;
            if (!body) {
                const {data} = await axios.get(`/api/v1/orders/${order.orderId}/decision-context`, config);
                if (current.current !== scope || controller.signal.aborted) { return false; }
                body = {...changes, requestId: crypto.randomUUID(), expectedVersions: Object.fromEntries(data.lines.map(line => [line.orderId, line.version]))};
                attempt.current = body;
            }
            const {data} = await axios.post(`/api/v1/orders/${order.orderId}/decision`, body, config);
            if (data?.requestId !== body.requestId || data.orderId !== order.orderId || data.status !== body.status) {
                throw new Error("Order decision receipt is unavailable");
            }
            if (current.current !== scope || controller.signal.aborted) { return false; }
            attempt.current = null; setState({scope}); return true;
        } catch (error) {
            if (current.current !== scope || controller.signal.aborted) { return false; }
            const status = error.response?.status, uncertain = Boolean(body) && (!status || status >= 500);
            if (!uncertain) { attempt.current = null; }
            setState({scope, error: status === 409 ? "OrderQueueDecisionConflict" : uncertain ? "OrderQueueDecisionUncertain" : "OrderQueueDecisionFailed", uncertain});
            return false;
        } finally { if (active.current === controller) { active.current = null; } }
    };
    return {...(state.scope === scope ? state : {}), submit};
}
